/**
 * DeviceDetector - Utility for detecting device types and capabilities
 *
 * Provides methods to detect:
 * - iOS devices (iPhone, iPad, iPod)
 * - Android devices
 * - Mobile vs Tablet
 * - Safari browser on iOS
 * - Standalone app mode
 */
export class DeviceDetector {
  constructor(userAgent = null) {
    this.userAgent = userAgent || (typeof navigator !== 'undefined' ? (navigator.userAgent || navigator.vendor || window.opera) : '');
    this._cache = {};
  }

  /**
   * Check if the current device is iOS
   * @returns {boolean}
   */
  isIOS() {
    if (this._cache.isIOS === undefined) {
      this._cache.isIOS = /iPad|iPhone|iPod/.test(this.userAgent) && !window.MSStream;
    }
    return this._cache.isIOS;
  }

  /**
   * Check if the current device is Android
   * @returns {boolean}
   */
  isAndroid() {
    if (this._cache.isAndroid === undefined) {
      this._cache.isAndroid = /android/i.test(this.userAgent);
    }
    return this._cache.isAndroid;
  }

  /**
   * Check if the current browser is Safari on iOS
   * Native Smart App Banner only works in Safari, not Chrome/Firefox on iOS
   * @returns {boolean}
   */
  isIOSSafari() {
    if (this._cache.isIOSSafari === undefined) {
      if (!this.isIOS()) {
        this._cache.isIOSSafari = false;
      } else {
        this._cache.isIOSSafari = /Safari/.test(this.userAgent) && !/CriOS|FxiOS|OPiOS|Mercury/.test(this.userAgent);
      }
    }
    return this._cache.isIOSSafari;
  }

  /**
   * Check if the current device is a tablet
   * @returns {boolean}
   */
  isTablet() {
    if (this._cache.isTablet === undefined) {
      this._cache.isTablet = /iPad/.test(this.userAgent) ||
                              (/Android/.test(this.userAgent) && !/Mobile/.test(this.userAgent));
    }
    return this._cache.isTablet;
  }

  /**
   * Check if the current device is mobile (excludes tablets)
   * @returns {boolean}
   */
  isMobile() {
    if (this._cache.isMobile === undefined) {
      if (typeof navigator === 'undefined') {
        this._cache.isMobile = false;
      } else if (this.isTablet()) {
        this._cache.isMobile = false;
      } else {
        this._cache.isMobile = this.isIOS() || this.isAndroid();
      }
    }
    return this._cache.isMobile;
  }

  /**
   * Check if running in standalone app mode (PWA)
   * @returns {boolean}
   */
  isStandalone() {
    if (this._cache.isStandalone === undefined) {
      if (typeof window === 'undefined') {
        this._cache.isStandalone = false;
      } else {
        this._cache.isStandalone = window.navigator.standalone === true ||
                                   window.matchMedia('(display-mode: standalone)').matches;
      }
    }
    return this._cache.isStandalone;
  }
}

// Singleton instance for convenience
export const deviceDetector = new DeviceDetector();
